import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowsClockwise, Warning, CheckCircle, Clock, CurrencyEur, Users, TrendUp, CaretDown, CaretUp, FileCsv, XCircle, PencilSimple, MagnifyingGlass, Funnel, Receipt } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import { fmtDate as fmtDatePt } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;
const STATUS_STYLES = {
  em_dia: { label: 'Em dia', color: 'bg-green-50 text-green-700 border-green-200' },
  atrasado: { label: 'Atrasado', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  vencido: { label: 'Vencido', color: 'bg-red-50 text-red-700 border-red-200' },
  inativo: { label: 'Inativo', color: 'bg-gray-100 text-gray-500 border-gray-200' },
  a_cancelar: { label: 'A cancelar', color: 'bg-orange-50 text-orange-600 border-orange-200' },
};

function SummaryCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="bg-white border border-brand-border rounded-2xl p-4" data-testid={`summary-${label.toLowerCase().replace(/ /g, '-')}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${color}`}><Icon size={16} weight="duotone" /></div>
        <span className="text-[10px] text-brand-muted uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-xl font-heading font-semibold text-brand-text">{value}</p>
      {sub && <p className="text-[10px] text-brand-muted mt-0.5">{sub}</p>}
    </div>
  );
}

function PaymentHistory({ customerId, onClose }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const { data } = await axios.get(`${API}/api/billing/payments/${customerId}`); setPayments(data); } catch {} finally { setLoading(false); }
    })();
  }, [customerId]);
  return (
    <div data-testid="payment-history">
      {loading ? <p className="text-sm text-brand-muted py-4">A carregar...</p> : payments.length === 0 ? (
        <p className="text-sm text-brand-muted py-4">Sem pagamentos registados.</p>
      ) : (
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {payments.map(p => (
            <div key={p.stripe_invoice_id} className="flex items-center justify-between p-3 bg-brand-bg/50 rounded-xl" data-testid={`payment-${p.stripe_invoice_id}`}>
              <div>
                <p className="text-sm text-brand-text">{fmtDatePt(p.date)}</p>
                <p className="text-[10px] text-brand-muted">{p.customer_email}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-brand-text">{p.amount?.toFixed(2)}€</p>
                <span className={`text-[9px] px-2 py-0.5 rounded-full ${p.paid ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
                  {p.paid ? 'Pago' : p.status}
                </span>
              </div>
              {p.invoice_pdf && (
                <a href={p.invoice_pdf} target="_blank" rel="noreferrer" className="ml-2 text-brand-green hover:text-brand-green-hover" title="Ver fatura">
                  <Receipt size={16} />
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function BillingPage() {
  useRequireAdmin();
  const [subs, setSubs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [editSub, setEditSub] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [configStatus, setConfigStatus] = useState(null);

  const fetch = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      if (planFilter) params.set('plan', planFilter);
      const [sRes, mRes, cRes] = await Promise.all([
        axios.get(`${API}/api/billing/subscriptions?${params}`),
        axios.get(`${API}/api/billing/summary?month=${month}`),
        axios.get(`${API}/api/billing/config-status`),
      ]);
      setSubs(sRes.data);
      setSummary(mRes.data);
      setConfigStatus(cRes.data);
    } catch {} finally { setLoading(false); }
  }, [search, statusFilter, planFilter, month]);
  useEffect(() => { fetch(); }, [fetch]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API}/api/billing/sync`);
      if (data.error) toast.error(data.error);
      else toast.success(`${data.synced} subscrições sincronizadas`);
      fetch();
    } catch { toast.error('Erro ao sincronizar'); }
    finally { setSyncing(false); }
  };

  const handleCancel = async (subId) => {
    if (!window.confirm('Cancelar esta subscrição no fim do período?')) return;
    try {
      await axios.post(`${API}/api/billing/subscriptions/${subId}/cancel`);
      toast.success('Subscrição marcada para cancelamento');
      fetch();
    } catch { toast.error('Erro ao cancelar'); }
  };

  const handleEditSave = async () => {
    const payload = { ...editForm, amount: editForm.amount === '' || editForm.amount === null || editForm.amount === undefined ? editForm.amount : Number(editForm.amount) };
    try {
      await axios.put(`${API}/api/billing/subscriptions/${editSub.stripe_sub_id}`, payload);
      toast.success('Subscrição atualizada');
      setEditSub(null);
      fetch();
    } catch { toast.error('Erro ao guardar'); }
  };

  const exportCsv = async () => {
    try {
      const r = await axios.get(`${API}/api/billing/export?month=${month}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url; a.download = `billing_${month}.csv`; a.click();
      toast.success('Exportado');
    } catch { toast.error('Erro ao exportar'); }
  };

  const overdueCount = subs.filter(s => s.status === 'atrasado' || s.status === 'vencido').length;

  if (loading) return <div className="text-center py-12 text-brand-muted">A carregar...</div>;

  return (
    <div className="fade-in" data-testid="billing-page">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="font-heading font-medium text-xl text-brand-text">Billing</h2>
          <p className="text-xs text-brand-muted mt-0.5">Gestão de subscrições e pagamentos</p>
        </div>
        <div className="flex gap-2">
          <button onClick={exportCsv} className="border border-brand-border text-brand-muted rounded-full px-3 py-2 text-xs flex items-center gap-1 hover:bg-brand-bg" data-testid="export-csv-btn">
            <FileCsv size={14} /> Exportar CSV
          </button>
          <button onClick={handleSync} disabled={syncing} className="bg-brand-green text-white rounded-full px-4 py-2 text-xs font-medium flex items-center gap-1.5 disabled:opacity-50" data-testid="sync-stripe-btn">
            <ArrowsClockwise size={14} className={syncing ? 'animate-spin' : ''} /> {syncing ? 'A sincronizar...' : 'Sincronizar Stripe'}
          </button>
        </div>
      </div>

      {/* Stripe config status */}
      {configStatus && !configStatus.configured && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-2xl mb-4 text-sm text-amber-700" data-testid="stripe-config-warning">
          <Warning size={18} weight="fill" />
          <div>
            <p className="font-medium">Stripe não configurado</p>
            <p className="text-xs mt-0.5">Define a variável <code className="bg-amber-100 px-1 rounded">STRIPE_SECRET_KEY</code> nos Secrets com a tua chave Stripe (sk_live_...).</p>
            {configStatus.key_length > 0 && <p className="text-[10px] mt-0.5">Chave detectada: {configStatus.key_prefix}{configStatus.key_suffix} ({configStatus.key_length} chars), fonte: {configStatus.source}</p>}
          </div>
        </div>
      )}

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <SummaryCard icon={Users} label="Ativas" value={summary.total_active} color="bg-green-50 text-green-600" />
          <SummaryCard icon={Warning} label="Em atraso" value={summary.total_overdue} color="bg-amber-50 text-amber-600" />
          <SummaryCard icon={CurrencyEur} label="Receita" value={`${summary.total_revenue}€`} sub={summary.month} color="bg-brand-green/10 text-brand-green" />
          <SummaryCard icon={TrendUp} label="Pagamentos" value={summary.new_payments} sub={`${summary.cancellations} cancelamentos`} color="bg-purple-50 text-purple-600" />
        </div>
      )}

      {/* Month selector */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="rounded-xl border-brand-border text-sm w-auto" data-testid="billing-month" />
        <div className="relative flex-1 min-w-[150px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Pesquisar cliente..." className="pl-8 rounded-xl border-brand-border text-sm" data-testid="billing-search" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border border-brand-border rounded-xl px-3 py-2 text-xs bg-white" data-testid="billing-status-filter">
          <option value="">Todos os estados</option>
          <option value="em_dia">Em dia</option>
          <option value="atrasado">Atrasado</option>
          <option value="vencido">Vencido</option>
          <option value="inativo">Inativo</option>
        </select>
        <select value={planFilter} onChange={e => setPlanFilter(e.target.value)} className="border border-brand-border rounded-xl px-3 py-2 text-xs bg-white" data-testid="billing-plan-filter">
          <option value="">Todos os planos</option>
          <option value="month">Mensal</option>
          <option value="quarter">Trimestral</option>
          <option value="semester">Semestral</option>
          <option value="year">Anual</option>
        </select>
      </div>

      {/* Overdue alert */}
      {overdueCount > 0 && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-2xl mb-4 text-sm text-amber-700" data-testid="overdue-alert">
          <Warning size={18} weight="fill" />
          <span><strong>{overdueCount}</strong> cliente{overdueCount > 1 ? 's' : ''} com pagamento atrasado</span>
        </div>
      )}

      {/* Subscriptions list */}
      {subs.length === 0 ? (
        <div className="text-center py-16 bg-white border border-brand-border rounded-2xl">
          <CurrencyEur size={48} className="mx-auto text-brand-placeholder mb-4" weight="duotone" />
          <p className="text-brand-muted text-sm">Sem subscrições.</p>
          <p className="text-brand-placeholder text-xs mt-1">Sincroniza com o Stripe para carregar os dados.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {subs.map(sub => {
            const st = STATUS_STYLES[sub.status] || STATUS_STYLES.inativo;
            return (
              <div key={sub.stripe_sub_id} className={`bg-white border rounded-2xl p-4 ${sub.status === 'atrasado' || sub.status === 'vencido' ? 'border-amber-200' : 'border-brand-border'}`} data-testid={`sub-${sub.stripe_sub_id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-medium text-brand-text truncate">{sub.customer_name || sub.customer_email}</h3>
                      <span className={`text-[9px] px-2 py-0.5 rounded-full border ${st.color}`}>{st.label}</span>
                      {sub.cancel_at_period_end && <span className="text-[9px] px-2 py-0.5 rounded-full bg-orange-50 text-orange-600">A cancelar</span>}
                    </div>
                    <p className="text-[10px] text-brand-muted mt-0.5">{sub.customer_email}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-brand-text">{sub.amount?.toFixed(2)}€ <span className="text-[10px] font-normal text-brand-muted">/ {sub.plan_label}</span></p>
                    <p className="text-[10px] text-brand-muted">Renova: {sub.next_renewal}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  {sub.last_payment_date && <span className="text-[9px] text-brand-muted bg-brand-bg px-2 py-0.5 rounded-full">Último: {sub.last_payment_date} ({sub.last_payment_amount?.toFixed(2)}€)</span>}
                  <div className="flex-1" />
                  <button onClick={() => setSelectedCustomer(sub.stripe_customer_id)} className="text-[10px] text-brand-green hover:underline" data-testid={`view-history-${sub.stripe_sub_id}`}>Histórico</button>
                  <button onClick={() => { setEditSub(sub); setEditForm({ plan_type: sub.plan_type, amount: sub.amount, next_renewal: sub.next_renewal, status: sub.status }); }} className="text-brand-muted hover:text-brand-green p-1"><PencilSimple size={13} /></button>
                  {!sub.cancel_at_period_end && sub.status !== 'inativo' && (
                    <button onClick={() => handleCancel(sub.stripe_sub_id)} className="text-brand-muted hover:text-red-500 p-1" title="Cancelar"><XCircle size={13} /></button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Payment history dialog */}
      <Dialog open={!!selectedCustomer} onOpenChange={v => { if (!v) setSelectedCustomer(null); }}>
        <DialogContent className="rounded-3xl border-brand-border max-w-lg">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Histórico de Pagamentos</DialogTitle></DialogHeader>
          {selectedCustomer && <PaymentHistory customerId={selectedCustomer} />}
        </DialogContent>
      </Dialog>

      {/* Edit subscription dialog */}
      <Dialog open={!!editSub} onOpenChange={v => { if (!v) setEditSub(null); }}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Editar Subscrição</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-brand-muted mb-1 block">Plano</Label>
              <select value={editForm.plan_type || ''} onChange={e => setEditForm({ ...editForm, plan_type: e.target.value })} className="w-full border border-brand-border rounded-xl px-3 py-2 text-sm bg-white">
                <option value="month">Mensal</option><option value="quarter">Trimestral</option><option value="semester">Semestral</option><option value="year">Anual</option>
              </select>
            </div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Montante (€)</Label>
              <Input type="number" step="0.01" value={editForm.amount || ''} onChange={e => setEditForm({ ...editForm, amount: parseFloat(e.target.value) })} className="rounded-xl border-brand-border" />
            </div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Próxima renovação</Label>
              <Input type="date" value={editForm.next_renewal || ''} onChange={e => setEditForm({ ...editForm, next_renewal: e.target.value })} className="rounded-xl border-brand-border" />
            </div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Estado</Label>
              <select value={editForm.status || ''} onChange={e => setEditForm({ ...editForm, status: e.target.value })} className="w-full border border-brand-border rounded-xl px-3 py-2 text-sm bg-white">
                <option value="em_dia">Em dia</option><option value="atrasado">Atrasado</option><option value="vencido">Vencido</option><option value="inativo">Inativo</option>
              </select>
            </div>
            <button onClick={handleEditSave} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="save-edit-sub-btn">Guardar alterações</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
